import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useScrollAnimation from '../hooks/useScrollAnimation';

const Features = () => {
  const navigate = useNavigate();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const sectionRefs = useRef([]);

  const features = [
    {
      number: '01',
      title: 'Seamless Resident Experience',
      description: 'Make compliance easy for residents with our white-labelled portal:',
      items: [
        'Customized email messaging',
        'Simple policy upload and verification',
        'Automatic reminders before policy expiration',
        'Option to purchase coverage through your mobile-friendly interface'
      ],
      image: process.env.PUBLIC_URL + '/images/seamless-resident-experience.png'
    },
    {
      number: '02',
      title: 'Automated Policy Verification',
      description: 'Our AI-powered system verified resident insurance certificates in seconds, not hours:',
      items: [
        'Instant verification of coverage limits and terms',
        'Automatic detection of policy lapses',
        'Validation of additional insured requirements',
        'Integration with carrier databases'
      ],
      image: process.env.PUBLIC_URL + '/images/2 1.svg'
    },
    {
      number: '03',
      title: 'Real-time Compliance Dashboard',
      description: 'Monitor your entire portfolios insurance compliance status from a single dashboard. Get instant visibility into;',
      items: [
        'Active policies and coverage verifications',
        'Compliance rates by property and region',
        'Non-compliant residents and automated follow-ups',
        'Premium collection and captive performance metrics'
      ],
      image: process.env.PUBLIC_URL + '/images/3 1.svg'
    },
    {
      number: '04',
      title: 'Automated Charge Posting',
      description: 'Non-compliant residents are automatically enrolled and charges sync to your property management software:',
      items: [
        'Real-time integration with your property management system',
        'Automatic premium calculation and posting',
        'Configurable grace periods and notification workflows',
        'Complete audit trail for compliance reporting'
      ],
      image: process.env.PUBLIC_URL + '/images/4 1.svg'
    }
  ];

  // Call all hooks at the top level
  const [contentRef1, contentVisible1] = useScrollAnimation({ threshold: 0.2 });
  const [contentRef2, contentVisible2] = useScrollAnimation({ threshold: 0.2 });
  const [contentRef3, contentVisible3] = useScrollAnimation({ threshold: 0.2 });
  const [contentRef4, contentVisible4] = useScrollAnimation({ threshold: 0.2 });

  const contentRefs = [contentRef1, contentRef2, contentRef3, contentRef4];
  const contentVisibles = [contentVisible1, contentVisible2, contentVisible3, contentVisible4];

  // Detect which section is currently in view to update the image
  useEffect(() => {
    // Adjust rootMargin for mobile devices
    const isMobile = window.innerWidth <= 768;
    const observerOptions = {
      root: null,
      rootMargin: isMobile ? '-20% 0px -20% 0px' : '-40% 0px -40% 0px', // More lenient on mobile
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = sectionRefs.current.indexOf(entry.target);
          if (index !== -1) {
            setActiveImageIndex(index);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all section elements
    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    // Handle resize to update observer options
    const handleResize = () => {
      observer.disconnect();
      const isMobileNow = window.innerWidth <= 768;
      const newOptions = {
        root: null,
        rootMargin: isMobileNow ? '-20% 0px -20% 0px' : '-40% 0px -40% 0px',
        threshold: 0
      };
      const newObserver = new IntersectionObserver(observerCallback, newOptions);
      sectionRefs.current.forEach((ref) => {
        if (ref) newObserver.observe(ref);
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      sectionRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  return (
    <div className="features-wrapper">
      {/* Vertical Dividers */}
      <div className="features-divider features-divider-1"></div>
      <div className="features-divider features-divider-2"></div>
      <div className="features-divider features-divider-3"></div>
      <div className="features-divider features-divider-4"></div>
      <div className="features-divider features-divider-5"></div>

      {/* Content Column */}
      <div className="features-content-absolute">
        {features.map((feature, index) => {
          const contentRef = contentRefs[index];
          const contentVisible = contentVisibles[index];
          
          return (
            <div 
              className="feature-section-wrapper"
              key={index}
              ref={(el) => {
                sectionRefs.current[index] = el;
              }}
            >
              {/* Mobile Image - shown above each section */}
              <div className="feature-mobile-image">
                <img 
                  src={feature.image} 
                  alt={feature.title}
                  className="feature-image-mobile"
                />
              </div>

              <div 
                ref={contentRef}
                className={`feature-section-content ${contentVisible ? 'animate-slide-in-left' : 'opacity-0'}`}
              >
                <div className="feature-section-inner">
                  <div className="feature-section-text">
                    <div className="feature-detail-number">{feature.number}</div>
                    <h2 className="feature-detail-title">{feature.title}</h2>
                    <div className="feature-detail-description-wrapper">
                      <p className="feature-detail-description">{feature.description}</p>
                      <ul className="feature-detail-list">
                        {feature.items.map((item, idx) => (
                          <li 
                            key={idx}
                            className={contentVisible ? 'animate-fade-in' : 'opacity-0'}
                            style={{ transitionDelay: `${0.3 + idx * 0.1}s` }}
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <button 
                    className="btn-explore-feature"
                    onClick={() => navigate('/schedule-call')}
                  >
                    <span>Explore</span>
                    <span className="btn-explore-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M10 7L15 12L10 17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Sticky Image Container - positioned relative to features-wrapper (Desktop only) */}
      <div className="feature-detail-image-sticky">
        {features.map((feature, index) => (
          <img 
            key={index}
            src={feature.image} 
            alt={feature.title}
            className={`feature-image ${index === activeImageIndex ? 'active' : ''}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Features;
